import {
  FC,
  useEffect,
  useState,
  type FocusEvent,
  type PointerEvent,
} from "react";
import { UNDO_WINDOW_MS, useRedactedV2 } from "../../redacted/v2";
import { operationProgress } from "../../redacted/v2/operationSteps";
import "./PrivateOperationSteps.scss";

const TITLE = "Private transaction";
/** Shown until the session reports its first step. */
const FIRST_STEP = "Preparing your transaction";
const UNDO_RING = 160;

/** What runs before this action starts: the automatic return of the last one. */
const waitingText = (background?: "preparing" | "returning" | "tidying") =>
  background === "preparing"
    ? "Getting ready…"
    : "Finishing your last transaction…";
/** A new Spending account's first funding waits a short, random while. */
const SPENDING_WAIT = "Getting your new Spending account ready…";
/** How long a refused Undo says so. */
const TOO_LATE_MS = 2_000;

/**
 * Loading toast of a private action: the step that runs now as the title, in
 * words that fit every action, the time left and a bar that never stands
 * still. It only ever shows its own action. With undo
 * enabled on this device, the first seconds show a ring around "Undo";
 * hovering or focusing the toast pauses it (paused time is not progress).
 */
export const PrivateOperationSteps: FC<{ fallback: string }> = ({
  fallback,
}) => {
  const v2 = useRedactedV2();
  const { holdOperationUndo } = v2;
  const operation = v2.operation;
  const [, setTick] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [refusedAt, setRefusedAt] = useState<number>();
  // Read the clock on every render, so a new action never shows a stale time.
  const now = Date.now();
  const undoOpen =
    !!operation &&
    operation.undoUntil !== undefined &&
    !operation.cancelled &&
    (!!operation.undoHeld || now < operation.undoUntil);
  const tooLate = refusedAt !== undefined && now - refusedAt < TOO_LATE_MS;
  const running =
    tooLate ||
    (!!operation &&
      operation.finishedAt === undefined &&
      operation.phase !== "failed");
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(() => setTick((tick) => tick + 1), 100);
    return () => clearInterval(timer);
  }, [running]);
  // Hover and focus hold the countdown independently: it resumes only once
  // neither holds it, and never stays held after the toast goes away.
  const holding = undoOpen && (hovered || focused);
  const operationId = operation?.id;
  useEffect(() => {
    if (!holding || operationId === undefined) return;
    holdOperationUndo(true, operationId);
    return () => holdOperationUndo(false, operationId);
  }, [holding, operationId, holdOperationUndo]);

  if (!operation)
    return (
      <>
        {v2.modelB.background === "returning" ||
        v2.modelB.background === "tidying"
          ? waitingText(v2.modelB.background)
          : fallback}
      </>
    );

  // Only a real mouse hovers: a tap on a touch screen must not pause the
  // countdown until the next tap somewhere else.
  const pointer = {
    onPointerEnter: (event: PointerEvent<HTMLDivElement>) => {
      if (event.pointerType === "mouse") setHovered(true);
    },
    onPointerLeave: () => setHovered(false),
    onFocus: () => setFocused(true),
    onBlur: (event: FocusEvent<HTMLDivElement>) => {
      if (!event.currentTarget.contains(event.relatedTarget as Node | null))
        setFocused(false);
    },
  };

  if (operation.cancelled)
    return (
      <div className="private-steps" role="status" {...pointer}>
        <strong className="fs-14">{TITLE}</strong>
        <span className="fs-12 color-grey">Cancelling…</span>
      </div>
    );

  if (undoOpen) {
    // The ring and the seconds show the same time left; a pause freezes both.
    const left = operation.undoHeld
      ? (operation.undoLeftMs ?? UNDO_WINDOW_MS)
      : Math.max((operation.undoUntil ?? now) - now, 0);
    const share = Math.min(Math.max(left / UNDO_WINDOW_MS, 0), 1);
    return (
      <div className="private-steps" role="status" {...pointer}>
        <span className="private-steps__head">
          <span className="private-steps__text">
            <strong className="fs-14">{TITLE}</strong>
            <span className="fs-12 color-grey">
              {tooLate
                ? "Too late to undo"
                : operation.undoHeld
                  ? "Paused"
                  : `Starts in ${Math.max(1, Math.ceil(left / 1_000))} s`}
            </span>
          </span>
          <button
            type="button"
            className="private-steps__ring"
            onClick={() => {
              if (!v2.cancelOperation(operation.id)) setRefusedAt(Date.now());
            }}>
            <svg viewBox="0 0 64 30" aria-hidden="true">
              <rect
                className="private-steps__ring-track"
                x="1"
                y="1"
                width="62"
                height="28"
                rx="14"
              />
              <rect
                className="private-steps__ring-fill"
                x="1"
                y="1"
                width="62"
                height="28"
                rx="14"
                strokeDasharray={UNDO_RING}
                strokeDashoffset={(UNDO_RING * (1 - share)).toFixed(1)}
              />
            </svg>
            <span>Undo</span>
          </button>
        </span>
      </div>
    );
  }

  const done = operation.finishedAt !== undefined;
  const progress = operationProgress(operation, now);
  const pending = operation.phase === "pending";
  const showTime =
    !done && !operation.waiting && !pending && !tooLate && progress.showTime;
  // The title is the step that really runs, in words that fit every action.
  const status = tooLate
    ? "Too late to undo"
    : operation.waiting
      ? operation.waitingFor === "spending"
        ? SPENDING_WAIT
        : waitingText(v2.modelB.background)
      : pending
        ? "Waiting for confirmation…"
        : done
          ? "Done"
          : progress.finishing
            ? "Almost done…"
            : `${operation.statusText ?? FIRST_STEP}…`;
  return (
    <div
      className="private-steps private-steps--compact"
      role="status"
      {...pointer}>
      <span className="private-steps__head">
        <strong className="fs-14">{status}</strong>
        {showTime && (
          <span className="private-steps__time" aria-hidden="true">
            {Math.max(1, Math.round(progress.remainingMs / 1_000))} s
          </span>
        )}
      </span>
      <span className="private-steps__bar" aria-hidden="true">
        <span
          className={`private-steps__fill color-teal${done ? "" : " private-steps__fill--busy"}`}
          style={{ width: `${(progress.share * 100).toFixed(2)}%` }}
        />
      </span>
    </div>
  );
};
